import React, { useState, useEffect, useCallback } from 'react';
import { getRecentCheckins } from '../api';

/**
 * CheckInRecentList Component
 *
 * Displays a table of recent check-ins with limit selector (10, 25, 50)
 */
function CheckInRecentList({ refreshTrigger }) {
  const [checkins, setCheckins] = useState([]);
  const [limit, setLimit] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRecentCheckins = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getRecentCheckins(limit);
      if (result.success) {
        setCheckins(result.checkins || []);
        setError(null);
      } else {
        setError(result.message || 'Failed to load recent check-ins');
      }
    } catch (err) {
      setError(err.message || 'Failed to load recent check-ins');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  // Fetch when limit changes
  useEffect(() => {
    fetchRecentCheckins();
  }, [fetchRecentCheckins]);

  // Refresh when refreshTrigger changes (after a check-in)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchRecentCheckins();
    }
  }, [refreshTrigger, fetchRecentCheckins]);

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
  };

  return (
    <div className="recent-checkins-card">
      <div className="recent-checkins-header">
        <h3>Recent Check-ins</h3>
        <div className="limit-buttons">
          {[10, 25, 50].map((value) => (
            <button
              key={value}
              className={`limit-button ${limit === value ? 'active' : ''}`}
              onClick={() => handleLimitChange(value)}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="recent-checkins-loading">
          <div className="table-skeleton">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="skeleton-row" />
            ))}
          </div>
        </div>
      ) : error ? (
        <div className="recent-checkins-error">
          <p>{error}</p>
          <button onClick={fetchRecentCheckins} className="retry-button">
            Retry
          </button>
        </div>
      ) : checkins.length === 0 ? (
        <div className="recent-checkins-empty">
          <p>No check-ins recorded yet.</p>
        </div>
      ) : (
        <div className="recent-checkins-table-wrapper">
          <table className="recent-checkins-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Checked In</th>
              </tr>
            </thead>
            <tbody>
              {checkins.map((checkin, index) => (
                <tr key={`${checkin.userId}-${checkin.checkinTime}-${index}`}>
                  <td className="name-cell">
                    {checkin.firstname} {checkin.lastname}
                  </td>
                  <td className="email-cell">{checkin.email}</td>
                  <td className="time-cell">{formatDateTime(checkin.checkinTime)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CheckInRecentList;
